import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useCurrentUser } from '@/api/hooks';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
const MAX_RETRIES = 15;
const SUB_ID_KEY = 'alfie:push-sub-id';

function urlBase64ToBuffer(base64: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const buf = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < raw.length; ++i) view[i] = raw.charCodeAt(i);
  return buf;
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function bufferEqual(a: BufferSource, b: BufferSource): boolean {
  const av = ArrayBuffer.isView(a)
    ? new Uint8Array(a.buffer, a.byteOffset, a.byteLength)
    : new Uint8Array(a);
  const bv = ArrayBuffer.isView(b)
    ? new Uint8Array(b.buffer, b.byteOffset, b.byteLength)
    : new Uint8Array(b);
  if (av.length !== bv.length) return false;
  for (let i = 0; i < av.length; i++) if (av[i] !== bv[i]) return false;
  return true;
}

/**
 * Returns 'standalone' if the user is in the installed PWA (Android WebAPK
 * or iOS home-screen install), 'browser' otherwise. Stored on each upsert
 * so we can tell from the server which surface the subscription is bound to
 * — same Web Push endpoint can come from either surface and looks identical
 * in user-agent, but the OS-level notification toggle differs between them
 * on Android (Chrome app vs the WebAPK).
 */
function getDisplayMode(): 'standalone' | 'browser' {
  if (typeof window === 'undefined') return 'browser';
  if (window.matchMedia?.('(display-mode: standalone)').matches) return 'standalone';
  // iOS Safari: navigator.standalone is true when launched from home screen.
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone) {
    return 'standalone';
  }
  return 'browser';
}

function getStoredSubId(): string | null {
  try { return localStorage.getItem(SUB_ID_KEY); } catch { return null; }
}

function setStoredSubId(id: string) {
  try { localStorage.setItem(SUB_ID_KEY, id); } catch { /* no-op */ }
}

function clearStoredSubId() {
  try { localStorage.removeItem(SUB_ID_KEY); } catch { /* no-op */ }
}

// Returns true if the subscription was confirmed registered in push_subscriptions.
// On success, persists the server-assigned row ID to localStorage so the client
// knows it is subscribed without an async service-worker lookup.
async function ensureSubscribed(userId: string): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  if (!VAPID_PUBLIC_KEY) {
    console.warn('[push] VITE_VAPID_PUBLIC_KEY not set — skipping subscribe');
    return false;
  }
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;

  const reg = await navigator.serviceWorker.ready;
  const newKey = urlBase64ToBuffer(VAPID_PUBLIC_KEY);
  let sub = await reg.pushManager.getSubscription();

  // Drop subscription if VAPID key rotated — backend can't sign for old key.
  if (sub) {
    const existingKey = sub.options.applicationServerKey;
    if (!existingKey || !bufferEqual(existingKey, newKey)) {
      try { await sub.unsubscribe(); } catch { /* no-op */ }
      clearStoredSubId();
      sub = null;
    }
  }

  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: newKey,
      });
    } catch (e) {
      console.error('[push] pushManager.subscribe failed:', e);
      return false;
    }
  }

  const p256dh = sub.getKey('p256dh');
  const auth = sub.getKey('auth');
  if (!p256dh || !auth) return false;

  const { data, error } = await supabase.from('push_subscriptions').upsert(
    {
      user_id: userId,
      endpoint: sub.endpoint,
      p256dh: arrayBufferToBase64(p256dh),
      auth: arrayBufferToBase64(auth),
      user_agent: navigator.userAgent,
      last_seen_at: new Date().toISOString(),
      display_mode: getDisplayMode(),
    },
    { onConflict: 'endpoint' },
  ).select('id').single();

  if (error || !data) {
    if (error?.code === '42501') {
      // Endpoint is owned by a different user (device re-used after account switch).
      // Unsubscribe to release the endpoint; the next retry will get a fresh one.
      try { await sub.unsubscribe(); } catch { /* no-op */ }
      clearStoredSubId();
    } else {
      console.error('[push] subscription upsert failed:', error);
    }
    return false;
  }

  setStoredSubId(data.id);
  console.log('[push] subscribed and upserted for', userId, '— sub_id:', data.id);
  return true;
}

async function ensureUnsubscribed(): Promise<void> {
  clearStoredSubId();
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await supabase.from('push_subscriptions').delete().eq('endpoint', sub.endpoint);
  try {
    await sub.unsubscribe();
  } catch {
    /* no-op */
  }
}

/**
 * Keeps the browser's push subscription in sync:
 * - On mount / permission change to 'granted': calls ensureSubscribed with
 *   quadratic backoff retry (up to MAX_RETRIES times) until the upsert succeeds.
 * - On window focus: checks localStorage for the stored sub ID. If missing
 *   (reinstall, cleared storage), restarts the retry chain.
 * - Hourly keepalive: re-upserts so server-side row deletions self-heal.
 * - On permission revoked: clears stored sub ID and unsubscribes.
 */
export function usePushSubscription() {
  const { data: user } = useCurrentUser();
  const [permission, setPermission] = useState<string>(
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  );
  const retryHandle = useRef<ReturnType<typeof setTimeout> | null>(null);

  function clearRetry() {
    if (retryHandle.current) {
      clearTimeout(retryHandle.current);
      retryHandle.current = null;
    }
  }

  const attemptSubscribe = useCallback((userId: string, attempt = 0) => {
    ensureSubscribed(userId).then((ok) => {
      if (ok) return;
      if (attempt >= MAX_RETRIES) {
        console.warn('[push] giving up after', MAX_RETRIES, 'attempts');
        return;
      }
      const delay = Math.min(1000 * (attempt + 1) ** 2, 30_000);
      console.log(`[push] retry ${attempt + 1}/${MAX_RETRIES} in ${delay}ms`);
      retryHandle.current = setTimeout(() => attemptSubscribe(userId, attempt + 1), delay);
    }).catch((e) => {
      console.error('[push] ensureSubscribed failed:', e);
    });
  }, []);

  // Keep permission state in sync.
  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    function sync() { setPermission(Notification.permission); }
    window.addEventListener('focus', sync);
    window.addEventListener('alfie:permission-change', sync);
    return () => {
      window.removeEventListener('focus', sync);
      window.removeEventListener('alfie:permission-change', sync);
    };
  }, []);

  // Subscribe / unsubscribe when permission or user changes.
  useEffect(() => {
    if (!user) return;
    clearRetry();
    if (permission === 'granted') {
      attemptSubscribe(user.id);
    } else {
      ensureUnsubscribed().catch((e) => console.error('[push] ensureUnsubscribed failed:', e));
    }
    return clearRetry;
  }, [user?.id, permission, attemptSubscribe]);

  // Hourly keepalive: re-upsert so server-side row deletions self-heal in long sessions.
  useEffect(() => {
    if (!user || permission !== 'granted') return;
    const handle = setInterval(() => {
      clearRetry();
      attemptSubscribe(user.id);
    }, 60 * 60 * 1000);
    return () => clearInterval(handle);
  }, [user?.id, permission, attemptSubscribe]);

  // On focus: only retry if we have no stored sub ID (reinstall / cleared storage).
  // Server-side deletion is handled by the hourly keepalive.
  useEffect(() => {
    if (!user || permission !== 'granted') return;

    function onFocus() {
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
      if (!user) return;
      if (!getStoredSubId()) {
        clearRetry();
        attemptSubscribe(user.id);
      }
    }

    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user?.id, permission, attemptSubscribe]);

  // Endpoint rotation. The SW's `pushsubscriptionchange` handler re-subscribes and
  // posts here because only the app has a user session — push_subscriptions RLS is
  // keyed on auth.uid() and the SW holds nothing but the anon key. The stored sub
  // ID now points at a dead endpoint, so drop it before re-upserting; otherwise the
  // focus check above sees a stored ID and skips the repair.
  useEffect(() => {
    if (!user || permission !== 'granted') return;
    if (!('serviceWorker' in navigator)) return;

    function onMessage(event: MessageEvent) {
      if (event.data?.type !== 'push-subscription-changed') return;
      if (!user) return;
      clearStoredSubId();
      clearRetry();
      attemptSubscribe(user.id);
    }

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [user?.id, permission, attemptSubscribe]);
}
